import { tokenPairResponseSchema } from "./auth-tokens.js";
import { z } from "zod";
//#region src/session-transport.ts
/**
* The request header a browser client sends to select cookie transport. Present with the value
* `SESSION_TRANSPORT_COOKIE`, core-api sets `REFRESH_COOKIE_NAME` on every route that mints a
* session and reads it on refresh and logout. Absent, every route behaves as before: the token
* pair travels in the body. Lower-case, because Node lower-cases incoming header names.
*
* It is also the CSRF control's custom header: a cross-origin caller cannot add it without a CORS
* preflight, and on every request that carries it core-api refuses an `Origin` other than
* `https://` plus the request's `Host` before any handler runs (rulings R-3, R-23).
*/
const SESSION_TRANSPORT_HEADER = "x-zion-session-transport";
/** The one value `SESSION_TRANSPORT_HEADER` takes. Any other value, or no header, means body transport. */
const SESSION_TRANSPORT_COOKIE = "cookie";
/**
* The HttpOnly cookie that carries the DP refresh token under cookie transport. Set by core-api
* with `Secure; HttpOnly; SameSite=Strict; Path=/v1/auth; Max-Age=REFRESH_COOKIE_MAX_AGE_SECONDS`.
* `/v1/auth` is a reserved platform prefix, so a hosted app's backend at `/v1/<slug>/*` never
* receives it. Script never reads it; the browser attaches it to same-origin requests under
* `/v1/auth`.
*/
const REFRESH_COOKIE_NAME = "zion_refresh_token";
/**
* The cookie's lifetime on both planes: 30 days, the session row's expiry in core-api's
* `SessionService` (`expiresAt.setDate(+30)`) and in cp-auth-api's (`SESSION_TTL_MS`, 30 days in
* ms). 30 × 24 × 60 × 60 = 2592000. Every rotation sets a fresh cookie, so the cookie and the row
* it names expire together.
*/
const REFRESH_COOKIE_MAX_AGE_SECONDS = 2592e3;
/**
* The upper bound on a refresh token in a request body. A live token is 64 characters
* (`randomBytes(48).toString('base64url')`); 4096 refuses a hostile payload at the validation
* pipe and is unchanged from the pre-contract DTO.
*/
const REFRESH_TOKEN_MAX_LENGTH = 4096;
/**
* The body of `POST /v1/auth/refresh` and `POST /v1/auth/logout`.
*
* `refreshToken` is optional in the schema on both arms, because the schema cannot see the header
* that selects the arm. Under body transport (no header) the handler requires it and answers 400
* when it is absent (ruling R-4). Under cookie transport the cookie is the credential when
* present, and the body's `refreshToken` is the fallback (ruling R-12): a browser mid-migration
* holds a legacy token and no cookie. `.strict()`: an unknown key at any depth is a 400 from the
* validation pipe.
*/
const refreshRequestSchema = z.object({ refreshToken: z.string().min(1).max(REFRESH_TOKEN_MAX_LENGTH).optional() }).strict();
/**
* The body every session-minting route and `POST /v1/auth/refresh` return under cookie
* transport: `TokenPairResponse` without `refreshToken`, which travels in the cookie instead.
* `expiresAt` is the access token's expiry, ISO-8601. `forcePasswordChange` is present and true
* when the user must change the password before anything else. The server gates on it: the access
* token carries the claim `fpc`, and core-api refuses every route outside the change-password
* allowlist with 403 `password_change_required` until the change. `.strict()`: a body that still
* carries `refreshToken` fails to parse, which is how a client proves the server stripped it.
*/
const cookieSessionResponseSchema = z.object({
	accessToken: z.string().min(1),
	expiresAt: z.string().datetime(),
	forcePasswordChange: z.boolean().optional()
}).strict();
/**
* Either body a browser client can receive from a session-minting route or from refresh: a
* `CookieSessionResponse` from a core-api that honoured the transport header, or a
* `TokenPairResponse` from an N-1 core-api pod that did not (ruling R-10). The shell and
* core-api are separate Deployments on RollingUpdate, so a window of mixed pods exists on every
* data plane release. A client parses this union, stores a `refreshToken` it receives in the
* legacy `localStorage` key and stays on the body arm, and deletes that key on the first
* `CookieSessionResponse`. Both members are `.strict()`, so a body with an unknown key fails
* both arms.
*/
const sessionResponseSchema = z.union([cookieSessionResponseSchema, tokenPairResponseSchema]);
/**
* The HttpOnly cookie that carries the CP refresh token under cookie transport. Set by cp-auth-api
* with `Secure; HttpOnly; SameSite=Strict; Path=/v1/auth; Max-Age=REFRESH_COOKIE_MAX_AGE_SECONDS`
* and no `Domain`, so `admin.zion.witness.ai` and `admin.internal.witness.ai` hold separate
* sessions. The name follows the CP's `cp_token` cookie. It is not `REFRESH_COOKIE_NAME`: the two
* tokens are different objects (the CP's is 256-bit hex, the DP's 384-bit base64url) and a
* reader of a `Cookie` header must never confuse them. `Path=/v1/auth` covers the seven CP routes
* that mint or end a session: `login`, `refresh`, `change-password`, `oauth/exchange`,
* `passkey/login`, `invitations/accept`, and `logout`. It lives here beside `REFRESH_COOKIE_NAME`
* because cp-auth-api sets it and the console's dev fixture server sets it too, and one file
* holding both names is where a reader learns the two cookies differ.
*/
const CP_REFRESH_COOKIE_NAME = "cp_refresh_token";
/**
* The CP refresh cookie's `Path`. One home for the three sites that spell it: cp-auth-api's
* attribute constant, and the console's dev fixture server's set and clear. Reversing the D33
* default (a `__Host-` prefix forces `Path=/`) is then one edit here (ruling R-56, finding PA2-6).
*/
const CP_REFRESH_COOKIE_PATH = "/v1/auth";
/**
* The body every CP session-minting route and `POST /v1/auth/refresh` return under cookie
* transport: cp-auth-api's `CpTokenPair` without `refreshToken`, which travels in the cookie.
* `expiresIn` is the access token's lifetime in seconds, the field the CP has always sent (the
* DP sends `expiresAt`, an instant). `.strict()`: a body that still carries `refreshToken` fails
* this member and parses as `cpTokenPairResponseSchema` in the union, so the console cannot tell
* an N-1 pod from an interceptor that stopped stripping (ruling R-10); cp-auth-api's per-route
* `toStrictEqual` interface arms are the detector.
*/
const cpCookieSessionResponseSchema = z.object({
	accessToken: z.string().min(1),
	expiresIn: z.number().int().positive()
}).strict();
/**
* The body a CP session-minting route returns under body transport, and the body an N-1
* cp-auth-api pod returns under either transport during a roll: cp-auth-api's `CpTokenPair`
* as it is today. `.strict()` so an unknown key fails both members of the union below.
*/
const cpTokenPairResponseSchema = z.object({
	accessToken: z.string().min(1),
	refreshToken: z.string().min(1).max(REFRESH_TOKEN_MAX_LENGTH),
	expiresIn: z.number().int().positive()
}).strict();
/**
* Either body the CP console can receive from a session-minting route or from refresh: a
* `CpCookieSessionResponse` from a cp-auth-api that honoured the transport header, or a
* `CpTokenPairResponse` from an N-1 pod that did not (rulings R-10 and R-47 apply on the CP as on
* the DP). The console parses this union. A `refreshToken` it receives goes back to the legacy
* `localStorage` key and the tab stays on the body arm; the first `CpCookieSessionResponse`
* deletes that key. Narrow with `'refreshToken' in body`.
*/
const cpSessionResponseSchema = z.union([cpCookieSessionResponseSchema, cpTokenPairResponseSchema]);
//#endregion
export { CP_REFRESH_COOKIE_NAME, CP_REFRESH_COOKIE_PATH, REFRESH_COOKIE_MAX_AGE_SECONDS, REFRESH_COOKIE_NAME, REFRESH_TOKEN_MAX_LENGTH, SESSION_TRANSPORT_COOKIE, SESSION_TRANSPORT_HEADER, cookieSessionResponseSchema, cpCookieSessionResponseSchema, cpSessionResponseSchema, cpTokenPairResponseSchema, refreshRequestSchema, sessionResponseSchema };

//# sourceMappingURL=session-transport.js.map